import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Modal, Radio, Select, Space, Switch, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreateConstituencyMutation,
  useDeleteConstituencyMutation,
  useGetConstituenciesQuery,
  useGetStatesQuery,
  useImportConstituenciesMutation,
  useUpdateConstituencyMutation,
} from '@/modules/locations/locationsApi';
import type { ConstituencyMaster, CreateConstituencyPayload } from '@/types/location';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmPermanentLocationDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { useLocationBulkUpload } from './components/useLocationBulkUpload';
import styles from './Locations.module.scss';

type ConstituencyFormValues = CreateConstituencyPayload;
type StatusFilter = 'all' | 'active' | 'inactive';

export default function Constituencies() {
  const notify = useNotify();
  const [form] = Form.useForm<ConstituencyFormValues>();
  const { data, isLoading, isFetching, refetch } = useGetConstituenciesQuery();
  const { data: statesData, isLoading: isStatesLoading } = useGetStatesQuery({ status: 'active' });
  const [createConstituency, { isLoading: isCreating }] = useCreateConstituencyMutation();
  const [updateConstituency, { isLoading: isUpdating }] = useUpdateConstituencyMutation();
  const [deleteConstituency, { isLoading: isDeleting }] = useDeleteConstituencyMutation();
  const [importConstituencies, { isLoading: isImporting }] = useImportConstituenciesMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConstituency, setEditingConstituency] = useState<ConstituencyMaster | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [nameSearch, setNameSearch] = useState('');
  const [nameSearchDraft, setNameSearchDraft] = useState('');
  const [stateId, setStateId] = useState<string | undefined>();
  const [stateIdDraft, setStateIdDraft] = useState<string | undefined>();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [statusDraft, setStatusDraft] = useState<StatusFilter>('all');

  const hasActiveFilters = nameSearch.length > 0 || Boolean(stateId) || status !== 'all';

  const handleResetFilters = () => {
    setNameSearch('');
    setNameSearchDraft('');
    setStateId(undefined);
    setStateIdDraft(undefined);
    setStatus('all');
    setStatusDraft('all');
    setPage(1);
  };

  const { bulkUploadControls } = useLocationBulkUpload({
    entityLabel: 'Constituency',
    entityLabelPlural: 'Constituencies',
    exportPath: '/constituencies/export',
    templatePath: '/constituencies/export-template',
    exportFilename: 'constituencies.xlsx',
    templateFilename: 'constituencies-template.xlsx',
    importMutation: importConstituencies,
    isImporting,
    onImported: () => {
      void refetch();
    },
  });

  useGlobalLoader(isFetching && !isLoading);

  const constituencies = data?.results ?? [];
  const stateOptions = (statesData?.results ?? []).map((state) => ({
    label: state.name,
    value: state.id,
  }));

  const filteredConstituencies = useMemo(() => {
    const query = nameSearch.trim().toLowerCase();
    return constituencies.filter((item) => {
      if (stateId && item.stateId !== stateId) return false;
      if (status === 'active' && !item.isActive) return false;
      if (status === 'inactive' && item.isActive) return false;
      if (!query) return true;
      return (
        item.name.toLowerCase().includes(query) || item.slug.toLowerCase().includes(query)
      );
    });
  }, [constituencies, nameSearch, stateId, status]);

  useEffect(() => {
    if (!modalOpen) return;
    form.setFieldsValue({
      stateId: editingConstituency?.stateId,
      name: editingConstituency?.name ?? '',
      isActive: editingConstituency?.isActive ?? true,
    });
  }, [editingConstituency, form, modalOpen]);

  const openCreate = () => {
    setEditingConstituency(null);
    setModalOpen(true);
  };

  const openEdit = (constituency: ConstituencyMaster) => {
    setEditingConstituency(constituency);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingConstituency(null);
    form.resetFields();
  };

  const handleSubmit = async (values: ConstituencyFormValues) => {
    const body = { ...values, name: values.name.trim() };
    const result = await resolveMutationResult(
      editingConstituency
        ? updateConstituency({ id: editingConstituency.id, body })
        : createConstituency(body),
    );

    if (result.ok) {
      notify.success(
        result.message ?? (editingConstituency ? 'Constituency updated.' : 'Constituency created.'),
      );
      closeModal();
      return;
    }

    notify.error(result.message);
  };

  const handleDelete = async (constituency: ConstituencyMaster) => {
    const confirmed = await confirmPermanentLocationDelete(
      constituency.name,
      'All assemblies under this constituency',
    );
    if (!confirmed) return;

    const result = await resolveMutationResult(deleteConstituency(constituency.id));
    if (result.ok) {
      notify.success(result.message ?? 'Constituency deleted.');
      return;
    }

    notify.error(result.message);
  };

  const columns: ColumnsType<ConstituencyMaster> = [
    {
      title: 'CONSTITUENCY',
      dataIndex: 'name',
      key: 'name',
      filterIcon: (filtered) => (
        <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
      ),
      filteredValue: nameSearch ? [nameSearch] : null,
      filterDropdown: ({ close }) => (
        <ColumnFilterPanel
          onApply={() => {
            setNameSearch(nameSearchDraft.trim());
            setPage(1);
            close();
          }}
          onReset={() => {
            setNameSearchDraft('');
            setNameSearch('');
            setPage(1);
            close();
          }}
        >
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="Search name or slug"
            value={nameSearchDraft}
            onChange={(event) => setNameSearchDraft(event.target.value)}
            onPressEnter={() => {
              setNameSearch(nameSearchDraft.trim());
              setPage(1);
              close();
            }}
          />
        </ColumnFilterPanel>
      ),
      filterDropdownProps: {
        onOpenChange: (open) => {
          if (open) setNameSearchDraft(nameSearch);
        },
      },
      render: (name: string, record) => (
        <div>
          <div className={styles.nameCell}>{name}</div>
          <div className={styles.subText}>{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'STATE',
      dataIndex: 'stateName',
      key: 'stateName',
      filterIcon: (filtered) => (
        <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
      ),
      filteredValue: stateId ? [stateId] : null,
      filterDropdown: ({ close }) => (
        <ColumnFilterPanel
          onApply={() => {
            setStateId(stateIdDraft);
            setPage(1);
            close();
          }}
          onReset={() => {
            setStateIdDraft(undefined);
            setStateId(undefined);
            setPage(1);
            close();
          }}
        >
          <Select
            allowClear
            showSearch
            size="small"
            className={styles.filterSelect}
            placeholder="Select state"
            options={stateOptions}
            optionFilterProp="label"
            value={stateIdDraft}
            onChange={(value) => setStateIdDraft(value)}
          />
        </ColumnFilterPanel>
      ),
      filterDropdownProps: {
        onOpenChange: (open) => {
          if (open) setStateIdDraft(stateId);
        },
      },
      render: (stateName: string) => stateName || '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 140,
      filterIcon: (filtered) => (
        <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
      ),
      filteredValue: status !== 'all' ? [status] : null,
      filterDropdown: ({ close }) => (
        <ColumnFilterPanel
          onApply={() => {
            setStatus(statusDraft);
            setPage(1);
            close();
          }}
          onReset={() => {
            setStatusDraft('all');
            setStatus('all');
            setPage(1);
            close();
          }}
        >
          <Radio.Group
            value={statusDraft}
            onChange={(event) => setStatusDraft(event.target.value)}
            className={styles.statusRadio}
          >
            <Radio value="all">All</Radio>
            <Radio value="active">Active</Radio>
            <Radio value="inactive">Inactive</Radio>
          </Radio.Group>
        </ColumnFilterPanel>
      ),
      filterDropdownProps: {
        onOpenChange: (open) => {
          if (open) setStatusDraft(status);
        },
      },
      render: (isActive: boolean) => (
        <span className={isActive ? styles.statusActive : styles.statusInactive}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'UPDATED',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 140,
      align: 'center',
      render: (_value, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFilters ? (
              <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
                Reset
              </Button>
            ) : null}
            {bulkUploadControls}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add constituency
            </Button>
          </>
        }
      />

      <div className={styles.tableCard}>
        <Table<ConstituencyMaster>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={filteredConstituencies}
          pagination={{
            current: page,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </div>

      <Modal
        title={editingConstituency ? 'Edit Constituency' : 'Add Constituency'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        confirmLoading={isCreating || isUpdating}
        destroyOnClose
      >
        <Form<ConstituencyFormValues>
          form={form}
          layout="vertical"
          initialValues={{ isActive: true }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label="State"
            name="stateId"
            rules={[{ required: true, message: 'Please select state.' }]}
          >
            <Select
              showSearch
              loading={isStatesLoading}
              options={stateOptions}
              optionFilterProp="label"
              placeholder="Select state"
            />
          </Form.Item>
          <Form.Item
            label="Constituency"
            name="name"
            rules={[{ required: true, message: 'Please enter constituency name.' }]}
          >
            <Input placeholder="Enter constituency name" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
